import { useEffect, useMemo, useState } from 'react';
import { parseInstantMs } from './calendar-date';

/**
 * The pressure-test hold clock (PT-3), driven by the SERVER's timestamps.
 *
 * WHY NOT `useShiftElapsed` (lib/shift-clock.ts). That hook counts from a
 * server timestamp too, but against the phone's own `Date.now()` with no
 * correction, and returns null while the phone runs behind the server. A hold
 * is timed to the minute against a regulator's sheet, and a phone whose clock
 * is two minutes fast would show a 10-minute hold as done at eight. So this
 * keeps one more number — how far the phone's clock is from the server's — and
 * shift-clock.ts is left as it is.
 *
 * WHERE THE OFFSET COMES FROM. POST /hold/start and /hold/stop answer
 * `server_now`. The offset is that instant minus the midpoint of the request
 * (when it went out and when it came back), so a slow round trip is split
 * rather than all charged to one side. It is a property of the DEVICE, not of
 * a test, so one value serves every hold; it is kept in sessionStorage so a
 * refresh mid-hold keeps it. Absent (a fresh tab that has started nothing),
 * the offset is 0 — the device clock, which is what every other timer in the
 * app uses — and the hold is still counted from the server's started_at.
 *
 * NOTHING HERE IS SENT. The start and end instants are the server's; the
 * client only reads them. A stopped hold's duration is ended_at − started_at,
 * both server stamps, and does not involve the device clock at all.
 */

const SKEW_KEY = 'fieldiq.pt.serverClockOffsetMs';

/** server − device, in ms. Every accessor is wrapped: storage may throw or be empty. */
export function readServerClockOffset(): number {
  try {
    const raw = sessionStorage.getItem(SKEW_KEY);
    if (raw === null) return 0;
    const n = Number(raw);
    // A malformed value is discarded, not coerced.
    return Number.isFinite(n) && Math.abs(n) < 7 * 24 * 3600 * 1000 ? n : 0;
  } catch {
    return 0;
  }
}

/**
 * Record the offset from a response carrying `server_now`. `sentAt` and
 * `receivedAt` are the device's clock either side of the request. Returns the
 * offset, or null when `server_now` did not parse (nothing is stored then).
 */
export function recordServerClockOffset(serverNow: unknown, sentAt: number, receivedAt: number): number | null {
  const server = parseInstantMs(serverNow);
  if (server === null || !Number.isFinite(sentAt) || !Number.isFinite(receivedAt)) return null;
  const offset = Math.round(server - (sentAt + receivedAt) / 2);
  try {
    sessionStorage.setItem(SKEW_KEY, String(offset));
  } catch {
    // The clock still runs from started_at; it just falls back to the device's.
  }
  return offset;
}

export interface HoldClock {
  /** 'idle' — not started; 'holding' — started, not stopped; 'stopped' — both stamps present. */
  phase: 'idle' | 'holding' | 'stopped';
  /** Whole seconds held, or null when not started (or a stamp did not parse). */
  elapsedSeconds: number | null;
  /** hold_minutes × 60, or null when the test has no hold length. */
  totalSeconds: number | null;
  /** total − elapsed, floored at 0; null without both. */
  remainingSeconds: number | null;
  /** True once the elapsed time reaches the hold length. */
  holdMet: boolean;
}

/** Pure, so it can be tested without a clock. `nowMs` is the device's; `offsetMs` corrects it to the server's. */
export function holdClockAt(
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
  holdMinutes: number | null | undefined,
  nowMs: number,
  offsetMs: number,
): HoldClock {
  const start = parseInstantMs(startedAt);
  const end = parseInstantMs(endedAt);
  const totalSeconds = typeof holdMinutes === 'number' && Number.isFinite(holdMinutes) && holdMinutes > 0 ? holdMinutes * 60 : null;
  if (start === null) {
    return { phase: 'idle', elapsedSeconds: null, totalSeconds, remainingSeconds: null, holdMet: false };
  }
  const phase = end === null ? 'holding' : 'stopped';
  const until = end ?? nowMs + offsetMs;
  // A start a little in the "future" (clock not yet corrected) reads as 0, not as a negative or a gap.
  const elapsedSeconds = Math.max(0, Math.floor((until - start) / 1000));
  const remainingSeconds = totalSeconds === null ? null : Math.max(0, totalSeconds - elapsedSeconds);
  return { phase, elapsedSeconds, totalSeconds, remainingSeconds, holdMet: totalSeconds !== null && elapsedSeconds >= totalSeconds };
}

/** Ticks once a second while holding; frozen otherwise. */
export function useHoldClock(
  startedAt: string | null | undefined,
  endedAt: string | null | undefined,
  holdMinutes: number | null | undefined,
): HoldClock {
  const [now, setNow] = useState(() => Date.now());
  const holding = parseInstantMs(startedAt) !== null && parseInstantMs(endedAt) === null;
  useEffect(() => {
    if (!holding) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [holding]);
  // Read on every tick: a hold started in this tab records the offset after mount.
  const offset = readServerClockOffset();
  return useMemo(() => holdClockAt(startedAt, endedAt, holdMinutes, now, offset), [startedAt, endedAt, holdMinutes, now, offset]);
}

/**
 * `mm:ss`, minutes not wrapped at 60 (a 240-minute hold reads `240:00`).
 * Absent is an em dash — never `00:00` for a hold whose start we do not know.
 */
export function formatHoldTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.floor(seconds);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
